/**
 * DocumentCenter.js — Complete Document Management with Edit support
 *
 * All three tabs now have Edit (pencil) action:
 *   My Documents  → Edit: update name/type/category/expiry_date
 *   Policies      → Edit (HR): update policy name/description/requires_ack
 *   Requests      → Edit (HR): update due_date/description on pending requests
 *
 * Other features:
 *   My Documents:  Upload, View, Download, Delete
 *   Policies:      Upload (HR), View, Download, Acknowledge, Delete (HR)
 *   Requests:      New Request (HR), Upload to fulfil (Employee), Cancel (HR)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Spinner,
    Modal, Form, Alert, Tabs, Tab, InputGroup, Row, Col,
} from 'react-bootstrap';
import {
    FileText, Upload, Download, Trash2, Search,
    CheckCircle, Plus, X, ExternalLink, Send,
    Users, Edit2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { openDoc, downloadDoc } from '../../utils/docUrl';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (d) => !d ? '—'
    : new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
const fmtSize = (b) => !b ? '—'
    : b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

const ADMIN_ROLES = ['admin','super-admin','hr-manager','account-manager'];

const StatusBadge = ({ status }) => {
    const map = {
        fulfilled: ['success','✓ Fulfilled'],
        cancelled: ['secondary','Cancelled'],
        pending:   ['warning','⏳ Pending'],
    };
    const [bg, label] = map[status] || ['secondary', status];
    return <Badge bg={bg} text={bg==='warning'?'dark':undefined} style={{fontSize:10}}>{label}</Badge>;
};

const DOC_TYPES = [
    'Offer Letter','Hike Letter','Appraisal Letter','Experience Letter',
    'Salary Certificate','PAN Card','Aadhar Card','Payslip','Tax Document',
    'Relieving Letter','Other',
];
const DOC_CATEGORIES = ['general','certificate','id_proof','tax','offer_letter','other'];
const REQUEST_TYPES  = [
    'PAN Card','Aadhar Card','Passport','Driving License','Educational Certificate',
    'Experience Letter','Bank Details','Address Proof','Medical Certificate','Other',
];

// ─── Upload My Document Modal ─────────────────────────────────────────────────
const UploadDocModal = ({ show, onHide, onSuccess, requestId=null, requestType='' }) => {
    const [form, setForm]     = useState({});
    const [file, setFile]     = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show) {
            setForm({ document_name:'', document_type:requestType||'', category:'general', expiry_date:'' });
            setFile(null);
        }
    }, [show, requestType]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error('Please select a file');
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('document_name',  form.document_name || file.name);
            fd.append('document_type',  form.document_type || 'General');
            fd.append('category',       form.category || 'general');
            if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
            if (requestId)        fd.append('request_id',  requestId);
            await axiosInstance.post('/documents/upload', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
            toast.success('Document uploaded successfully');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Upload failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    {requestId ? `Upload: ${requestType}` : 'Upload Document'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {requestId && (
                        <Alert variant="info" className="small py-2 mb-3">
                            Uploading to fulfil HR request: <strong>{requestType}</strong>
                        </Alert>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">File *</Form.Label>
                        <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.webp"
                            onChange={e => setFile(e.target.files[0])} required/>
                        <Form.Text className="text-muted">PDF, Images, Word — max 5 MB</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Document Name</Form.Label>
                        <Form.Control placeholder="e.g. Offer Letter 2024"
                            value={form.document_name||''} onChange={e => set('document_name', e.target.value)}/>
                    </Form.Group>
                    <Row className="g-2">
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Document Type</Form.Label>
                            <Form.Select value={form.document_type||''} onChange={e => set('document_type', e.target.value)}>
                                <option value="">Select…</option>
                                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Category</Form.Label>
                            <Form.Select value={form.category||'general'} onChange={e => set('category', e.target.value)}>
                                {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>
                    <Form.Group className="mt-2">
                        <Form.Label className="small fw-bold">Expiry Date (optional)</Form.Label>
                        <Form.Control type="date" value={form.expiry_date||''} onChange={e => set('expiry_date', e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Uploading…</> : <><Upload size={14} className="me-1"/>Upload</>}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── Edit My Document Modal ────────────────────────────────────────────────────
const EditDocModal = ({ show, onHide, onSuccess, doc }) => {
    const [form, setForm]     = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show && doc) {
            setForm({
                document_name: doc.document_name || '',
                document_type: doc.document_type || '',
                category:      doc.category      || 'general',
                description:   doc.description   || '',
                expiry_date:   doc.expiry_date   ? String(doc.expiry_date).slice(0,10) : '',
            });
        }
    }, [show, doc]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.document_name?.trim()) return toast.error('Document name is required');
        setSaving(true);
        try {
            await axiosInstance.patch(`/documents/${doc.id}`, form);
            toast.success('Document updated');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Update failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <Edit2 size={16} className="text-primary"/>Edit Document
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Document Name *</Form.Label>
                        <Form.Control value={form.document_name||''} onChange={e => set('document_name', e.target.value)} required/>
                    </Form.Group>
                    <Row className="g-2 mb-2">
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Document Type</Form.Label>
                            <Form.Select value={form.document_type||''} onChange={e => set('document_type', e.target.value)}>
                                <option value="">Select…</option>
                                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Category</Form.Label>
                            <Form.Select value={form.category||'general'} onChange={e => set('category', e.target.value)}>
                                {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control as="textarea" rows={2} value={form.description||''} onChange={e => set('description', e.target.value)} placeholder="Optional notes…"/>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Expiry Date</Form.Label>
                        <Form.Control type="date" value={form.expiry_date||''} onChange={e => set('expiry_date', e.target.value)}/>
                        {form.expiry_date && (
                            <Button variant="link" size="sm" className="p-0 mt-1 text-muted"
                                onClick={() => set('expiry_date', '')}>Clear expiry date</Button>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Saving…</> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── Upload Policy Modal (HR only) ────────────────────────────────────────────
const UploadPolicyModal = ({ show, onHide, onSuccess }) => {
    const [form, setForm]     = useState({ document_name:'', description:'', requires_ack:false });
    const [file, setFile]     = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show) { setForm({ document_name:'', description:'', requires_ack:false }); setFile(null); }
    }, [show]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file)                       return toast.error('Please select a file');
        if (!form.document_name?.trim()) return toast.error('Policy name is required');
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('file',          file);
            fd.append('document_name', form.document_name.trim());
            fd.append('description',   form.description || '');
            fd.append('requires_ack',  form.requires_ack ? 'true' : 'false');
            await axiosInstance.post('/documents/policy', fd, { headers:{ 'Content-Type':'multipart/form-data' }});
            toast.success('Policy uploaded and employees notified');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Upload failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Upload Company Policy</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Alert variant="info" className="small py-2 mb-3">All active employees will be notified.</Alert>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">File * (PDF recommended)</Form.Label>
                        <Form.Control type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} required/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Policy Name *</Form.Label>
                        <Form.Control placeholder="e.g. Leave Policy 2025" value={form.document_name} onChange={e => set('document_name', e.target.value)} required/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Description (optional)</Form.Label>
                        <Form.Control as="textarea" rows={2} placeholder="Brief summary…" value={form.description} onChange={e => set('description', e.target.value)}/>
                    </Form.Group>
                    <Form.Check type="switch" id="requires-ack"
                        label={<span className="small fw-bold">Require employee acknowledgement</span>}
                        checked={!!form.requires_ack} onChange={e => set('requires_ack', e.target.checked)}/>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Uploading…</> : <><Upload size={14} className="me-1"/>Upload Policy</>}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── Edit Policy Modal (HR only) ──────────────────────────────────────────────
const EditPolicyModal = ({ show, onHide, onSuccess, doc }) => {
    const [form, setForm]     = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show && doc) {
            setForm({
                document_name: doc.document_name || '',
                description:   doc.description   || '',
                requires_ack:  !!doc.requires_ack,
            });
        }
    }, [show, doc]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.document_name?.trim()) return toast.error('Policy name is required');
        setSaving(true);
        try {
            await axiosInstance.patch(`/documents/${doc.id}`, form);
            toast.success('Policy updated');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Update failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <Edit2 size={16} className="text-primary"/>Edit Policy
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Policy Name *</Form.Label>
                        <Form.Control value={form.document_name||''} onChange={e => set('document_name', e.target.value)} required/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control as="textarea" rows={2} value={form.description||''} onChange={e => set('description', e.target.value)}/>
                    </Form.Group>
                    <Form.Check type="switch" id="edit-requires-ack"
                        label={<span className="small fw-bold">Require employee acknowledgement</span>}
                        checked={!!form.requires_ack} onChange={e => set('requires_ack', e.target.checked)}/>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Saving…</> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── New Document Request Modal (HR only) ─────────────────────────────────────
const NewRequestModal = ({ show, onHide, onSuccess, employees=[] }) => {
    const [form, setForm]     = useState({ employee_id:'', document_type:'', description:'', due_date:'' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show) setForm({ employee_id:'', document_type:'', description:'', due_date:'' });
    }, [show]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.employee_id)           return toast.error('Please select an employee');
        if (!form.document_type?.trim()) return toast.error('Document type is required');
        setSaving(true);
        try {
            await axiosInstance.post('/documents/requests', {
                employee_id:   form.employee_id,
                document_type: form.document_type.trim(),
                description:   form.description || null,
                due_date:      form.due_date    || null,
            });
            toast.success('Document request sent to employee');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create request');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Send size={16} className="me-2 text-primary"/>Request Document from Employee
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Employee *</Form.Label>
                        <Form.Select value={form.employee_id} onChange={e => set('employee_id', e.target.value)} required>
                            <option value="">Select employee…</option>
                            {employees.map(emp => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.first_name} {emp.last_name}
                                    {emp.employee_code ? ` (${emp.employee_code})` : ''}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Document Type *</Form.Label>
                        <Form.Select value={form.document_type} onChange={e => set('document_type', e.target.value)}>
                            <option value="">Select…</option>
                            {REQUEST_TYPES.map(t => <option key={t}>{t}</option>)}
                        </Form.Select>
                        <Form.Control className="mt-2" placeholder="Or type custom document type…"
                            value={form.document_type} onChange={e => set('document_type', e.target.value)}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Description / Notes</Form.Label>
                        <Form.Control as="textarea" rows={2} placeholder="Additional instructions…"
                            value={form.description} onChange={e => set('description', e.target.value)}/>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Due Date (optional)</Form.Label>
                        <Form.Control type="date" min={new Date().toISOString().slice(0,10)}
                            value={form.due_date} onChange={e => set('due_date', e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Sending…</> : <><Send size={14} className="me-1"/>Send Request</>}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── Edit Request Modal (HR only) ────────────────────────────────────────────
const EditRequestModal = ({ show, onHide, onSuccess, req }) => {
    const [form, setForm]     = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (show && req) {
            setForm({
                document_type: req.document_type || '',
                description:   req.description   || '',
                due_date:      req.due_date       ? String(req.due_date).slice(0,10) : '',
            });
        }
    }, [show, req]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axiosInstance.patch(`/documents/requests/${req.id}/update`, form);
            toast.success('Request updated');
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Update failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <Edit2 size={16} className="text-primary"/>Edit Document Request
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Document Type</Form.Label>
                        <Form.Control value={form.document_type||''} onChange={e => set('document_type', e.target.value)}/>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Description / Notes</Form.Label>
                        <Form.Control as="textarea" rows={2} value={form.description||''} onChange={e => set('description', e.target.value)}/>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Due Date</Form.Label>
                        <Form.Control type="date" value={form.due_date||''} onChange={e => set('due_date', e.target.value)}/>
                        {form.due_date && (
                            <Button variant="link" size="sm" className="p-0 mt-1 text-muted" onClick={() => set('due_date', '')}>Clear due date</Button>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <><Spinner size="sm" className="me-1"/>Saving…</> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── Main DocumentCenter ──────────────────────────────────────────────────────
const DocumentCenter = () => {
    const { user } = useAuth();
    const isHR = ADMIN_ROLES.includes(user?.role);

    const [myDocs,     setMyDocs]     = useState([]);
    const [policies,   setPolicies]   = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [hrRequests, setHrRequests] = useState([]);
    const [employees,  setEmployees]  = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [search,     setSearch]     = useState('');
    const [activeTab,  setActiveTab]  = useState('my');

    // Upload modals
    const [showUploadDoc,    setShowUploadDoc]    = useState(false);
    const [showUploadPolicy, setShowUploadPolicy] = useState(false);
    const [showNewRequest,   setShowNewRequest]   = useState(false);
    const [uploadReqCtx,     setUploadReqCtx]     = useState(null);

    // Edit modals
    const [editDoc,     setEditDoc]     = useState(null);   // doc object to edit
    const [editPolicy,  setEditPolicy]  = useState(null);   // policy doc to edit
    const [editRequest, setEditRequest] = useState(null);   // request to edit

    const [deleting, setDeleting] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const calls = [
                axiosInstance.get('/documents/my'),
                axiosInstance.get('/documents/policies'),
                axiosInstance.get('/documents/requests/my'),
            ];
            if (isHR) {
                calls.push(axiosInstance.get('/documents/requests'));
                calls.push(axiosInstance.get('/employees'));
            }
            const [myRes, polRes, myReqRes, hrReqRes, empRes] = await Promise.all(
                calls.map(p => p.catch(() => ({ data:{ data:[] } })))
            );
            setMyDocs(myRes.data.data     || []);
            setPolicies(polRes.data.data  || []);
            setMyRequests(myReqRes.data.data || []);
            if (isHR) {
                setHrRequests(hrReqRes?.data?.data || []);
                const empData = empRes?.data?.data;
                setEmployees(Array.isArray(empData) ? empData : (empData?.employees || []));
            }
        } catch {
            toast.error('Failed to load documents');
        } finally { setLoading(false); }
    }, [isHR]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleDelete = async (docId) => {
        setDeleting(docId);
        try {
            await axiosInstance.delete(`/documents/${docId}`);
            toast.success('Document deleted');
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed');
        } finally { setDeleting(null); }
    };

    const handleAcknowledge = async (docId) => {
        try {
            await axiosInstance.post(`/documents/${docId}/acknowledge`);
            toast.success('Acknowledged');
            loadAll();
        } catch { toast.error('Failed to acknowledge'); }
    };

    const handleCancelRequest = async (reqId) => {
        try {
            await axiosInstance.patch(`/documents/requests/${reqId}/cancel`);
            toast.success('Request cancelled');
            loadAll();
        } catch { toast.error('Failed to cancel'); }
    };

    const handleFulfilRequest = (req) => {
        setUploadReqCtx({ id: req.id, document_type: req.document_type });
        setShowUploadDoc(true);
    };

    const filtered        = myDocs.filter(d =>
        `${d.document_name} ${d.document_type} ${d.category}`.toLowerCase().includes(search.toLowerCase())
    );
    const myPendingCount  = myRequests.filter(r => r.status === 'pending').length;
    const hrPendingCount  = isHR ? hrRequests.filter(r => r.status === 'pending').length : 0;

    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary"/>
            <p className="mt-2 text-muted small">Loading documents…</p>
        </div>
    );

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="mb-4">
                <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                    <FileText size={22} className="text-primary"/>Document Center
                </h4>
                <p className="text-muted small mb-0">
                    Manage your documents, company policies and HR document requests
                </p>
            </div>

            <Tabs activeKey={activeTab} onSelect={k => { setActiveTab(k); setSearch(''); }} className="mb-4">

                {/* ══ My Documents ══════════════════════════════════════════ */}
                <Tab eventKey="my" title={`My Documents (${myDocs.length})`}>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <InputGroup style={{maxWidth:360}}>
                            <InputGroup.Text><Search size={14}/></InputGroup.Text>
                            <Form.Control placeholder="Search documents…" value={search} onChange={e => setSearch(e.target.value)}/>
                        </InputGroup>
                        <Button variant="primary" size="sm" className="d-flex align-items-center gap-2"
                            onClick={() => { setUploadReqCtx(null); setShowUploadDoc(true); }}>
                            <Upload size={14}/>Upload Document
                        </Button>
                    </div>

                    {filtered.length === 0 ? (
                        <Alert variant="light" className="border text-center py-5">
                            <FileText size={32} className="text-muted mb-2 d-block mx-auto"/>
                            <div className="text-muted">No documents yet.</div>
                            <Button variant="outline-primary" size="sm" className="mt-3"
                                onClick={() => { setUploadReqCtx(null); setShowUploadDoc(true); }}>
                                <Upload size={14} className="me-1"/>Upload your first document
                            </Button>
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Table hover responsive className="align-middle small mb-0">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                        <th className="ps-3">Document</th>
                                        <th>Type</th>
                                        <th>Size</th>
                                        <th>Uploaded</th>
                                        <th>Expires</th>
                                        <th className="text-end pe-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(doc => (
                                        <tr key={doc.id}>
                                            <td className="ps-3">
                                                <div className="fw-medium">{doc.document_name || '—'}</div>
                                                <div className="text-muted" style={{fontSize:10}}>{doc.category || 'general'}</div>
                                            </td>
                                            <td>{doc.document_type || '—'}</td>
                                            <td>{fmtSize(doc.file_size)}</td>
                                            <td>{fmtDate(doc.uploaded_at)}</td>
                                            <td>
                                                {doc.expiry_date ? (
                                                    <span className={new Date(doc.expiry_date) < new Date() ? 'text-danger' : ''}>
                                                        {fmtDate(doc.expiry_date)}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="text-end pe-3">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <Button size="sm" variant="outline-primary" className="py-1" title="View"
                                                        onClick={() => openDoc(doc.file_path)}>
                                                        <ExternalLink size={13}/>
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary" className="py-1" title="Download"
                                                        onClick={() => downloadDoc(doc.file_path, doc.document_name)}>
                                                        <Download size={13}/>
                                                    </Button>
                                                    {/* EDIT button — opens EditDocModal */}
                                                    <Button size="sm" variant="outline-warning" className="py-1" title="Edit document details"
                                                        onClick={() => setEditDoc(doc)}>
                                                        <Edit2 size={13}/>
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" className="py-1" title="Delete"
                                                        disabled={deleting === doc.id}
                                                        onClick={() => handleDelete(doc.id)}>
                                                        {deleting === doc.id ? <Spinner size="sm"/> : <Trash2 size={13}/>}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </Tab>

                {/* ══ Policies ══════════════════════════════════════════════ */}
                <Tab eventKey="policies" title={`Policies (${policies.length})`}>
                    {isHR && (
                        <div className="d-flex justify-content-end mb-3">
                            <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-2"
                                onClick={() => setShowUploadPolicy(true)}>
                                <Plus size={14}/>Upload Policy Document
                            </Button>
                        </div>
                    )}

                    {policies.length === 0 ? (
                        <Alert variant="light" className="border text-center py-5">
                            <FileText size={32} className="text-muted mb-2 d-block mx-auto"/>
                            <div className="text-muted">No company policies published yet.</div>
                            {isHR && (
                                <Button variant="outline-primary" size="sm" className="mt-3"
                                    onClick={() => setShowUploadPolicy(true)}>
                                    <Plus size={14} className="me-1"/>Upload first policy
                                </Button>
                            )}
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Table hover responsive className="align-middle small mb-0">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                        <th className="ps-3">Policy</th>
                                        <th>Description</th>
                                        <th>Uploaded</th>
                                        <th>Acknowledgement</th>
                                        <th className="text-end pe-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {policies.map(doc => (
                                        <tr key={doc.id}>
                                            <td className="ps-3 fw-medium">{doc.document_name}</td>
                                            <td className="text-muted" style={{maxWidth:180}}>{doc.description || '—'}</td>
                                            <td>{fmtDate(doc.uploaded_at)}</td>
                                            <td>
                                                {doc.my_ack_at ? (
                                                    <span className="text-success d-flex align-items-center gap-1" style={{fontSize:12}}>
                                                        <CheckCircle size={13}/>{fmtDate(doc.my_ack_at)}
                                                    </span>
                                                ) : doc.requires_ack ? (
                                                    <Badge bg="warning" text="dark" style={{fontSize:10}}>Required</Badge>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td className="text-end pe-3">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <Button size="sm" variant="outline-primary" className="py-1" title="View"
                                                        onClick={() => openDoc(doc.file_path)}>
                                                        <ExternalLink size={13}/>
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary" className="py-1" title="Download"
                                                        onClick={() => downloadDoc(doc.file_path, doc.document_name)}>
                                                        <Download size={13}/>
                                                    </Button>
                                                    {!!doc.requires_ack && !doc.my_ack_at && (
                                                        <Button size="sm" variant="success" className="py-1 px-2" style={{fontSize:11}}
                                                            onClick={() => handleAcknowledge(doc.id)}>
                                                            <CheckCircle size={13} className="me-1"/>Acknowledge
                                                        </Button>
                                                    )}
                                                    {/* EDIT button for HR */}
                                                    {isHR && (
                                                        <Button size="sm" variant="outline-warning" className="py-1" title="Edit policy"
                                                            onClick={() => setEditPolicy(doc)}>
                                                            <Edit2 size={13}/>
                                                        </Button>
                                                    )}
                                                    {isHR && (
                                                        <Button size="sm" variant="outline-danger" className="py-1" title="Delete"
                                                            disabled={deleting === doc.id}
                                                            onClick={() => handleDelete(doc.id)}>
                                                            {deleting === doc.id ? <Spinner size="sm"/> : <Trash2 size={13}/>}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </Tab>

                {/* ══ Requests ══════════════════════════════════════════════ */}
                <Tab eventKey="requests" title={
                    <span>
                        Requests
                        {(myPendingCount + hrPendingCount) > 0 && (
                            <Badge bg="danger" className="ms-1" style={{fontSize:9}}>
                                {myPendingCount + hrPendingCount}
                            </Badge>
                        )}
                    </span>
                }>
                    {isHR && (
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <Button size="sm" variant="primary" className="d-flex align-items-center gap-1"
                                onClick={() => setShowNewRequest(true)}>
                                <Plus size={14}/>New Document Request
                            </Button>
                            <div className="text-muted small">
                                <Users size={13} className="me-1"/>
                                {hrRequests.filter(r => r.status==='pending').length} pending requests
                            </div>
                        </div>
                    )}

                    {(() => {
                        const requestsList = isHR ? hrRequests : myRequests;
                        if (requestsList.length === 0) return (
                            <Alert variant="light" className="border text-center py-5">
                                <CheckCircle size={32} className="text-success mb-2 d-block mx-auto"/>
                                <div className="text-muted">No document requests.</div>
                                {isHR && (
                                    <Button variant="outline-primary" size="sm" className="mt-3"
                                        onClick={() => setShowNewRequest(true)}>
                                        <Plus size={14} className="me-1"/>Create first request
                                    </Button>
                                )}
                            </Alert>
                        );

                        return (
                            <Card className="border-0 shadow-sm">
                                <Table hover responsive className="align-middle small mb-0">
                                    <thead className="bg-light">
                                        <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                            <th className="ps-3">Document Requested</th>
                                            {isHR && <th>Employee</th>}
                                            <th>Description</th>
                                            {!isHR && <th>Requested By</th>}
                                            <th>Due Date</th>
                                            <th>Status</th>
                                            <th className="text-end pe-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requestsList.map(req => {
                                            const isOverdue = req.status === 'pending' && req.due_date
                                                && new Date(req.due_date) < new Date();
                                            return (
                                                <tr key={req.id}>
                                                    <td className="ps-3 fw-medium">{req.document_type}</td>
                                                    {isHR && (
                                                        <td>
                                                            <div>{req.employee_name || '—'}</div>
                                                            {req.employee_code && <code style={{fontSize:10}}>{req.employee_code}</code>}
                                                        </td>
                                                    )}
                                                    <td className="text-muted" style={{maxWidth:150}}>{req.description || '—'}</td>
                                                    {!isHR && <td>{req.requested_by_name || 'HR'}</td>}
                                                    <td>
                                                        {req.due_date ? (
                                                            <span className={isOverdue ? 'text-danger fw-bold' : ''}>
                                                                {fmtDate(req.due_date)}{isOverdue && ' ⚠'}
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                    <td><StatusBadge status={req.status}/></td>
                                                    <td className="text-end pe-3">
                                                        <div className="d-flex justify-content-end gap-1">
                                                            {/* Employee: Upload to fulfil */}
                                                            {!isHR && req.status === 'pending' && (
                                                                <Button size="sm" variant="primary" className="py-1"
                                                                    onClick={() => handleFulfilRequest(req)}>
                                                                    <Upload size={13} className="me-1"/>Upload
                                                                </Button>
                                                            )}
                                                            {/* HR: Edit pending request */}
                                                            {isHR && req.status === 'pending' && (
                                                                <Button size="sm" variant="outline-warning" className="py-1" title="Edit request"
                                                                    onClick={() => setEditRequest(req)}>
                                                                    <Edit2 size={13}/>
                                                                </Button>
                                                            )}
                                                            {/* HR: Cancel pending request */}
                                                            {isHR && req.status === 'pending' && (
                                                                <Button size="sm" variant="outline-danger" className="py-1" title="Cancel request"
                                                                    onClick={() => handleCancelRequest(req.id)}>
                                                                    <X size={13}/>
                                                                </Button>
                                                            )}
                                                            {req.status === 'fulfilled' && (
                                                                <span className="text-success small d-flex align-items-center gap-1">
                                                                    <CheckCircle size={13}/>Done
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Card>
                        );
                    })()}
                </Tab>
            </Tabs>

            {/* ── Modals ── */}
            <UploadDocModal
                show={showUploadDoc}
                onHide={() => { setShowUploadDoc(false); setUploadReqCtx(null); }}
                onSuccess={loadAll}
                requestId={uploadReqCtx?.id}
                requestType={uploadReqCtx?.document_type || ''}
            />
            <UploadPolicyModal
                show={showUploadPolicy}
                onHide={() => setShowUploadPolicy(false)}
                onSuccess={loadAll}
            />
            <NewRequestModal
                show={showNewRequest}
                onHide={() => setShowNewRequest(false)}
                onSuccess={loadAll}
                employees={employees}
            />
            {/* Edit modals */}
            <EditDocModal
                show={!!editDoc}
                onHide={() => setEditDoc(null)}
                onSuccess={loadAll}
                doc={editDoc}
            />
            <EditPolicyModal
                show={!!editPolicy}
                onHide={() => setEditPolicy(null)}
                onSuccess={loadAll}
                doc={editPolicy}
            />
            <EditRequestModal
                show={!!editRequest}
                onHide={() => setEditRequest(null)}
                onSuccess={loadAll}
                req={editRequest}
            />
        </div>
    );
};

export default DocumentCenter;
